// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (skipper), § Decisions
// ==============================================
// /dashboard/papers/requests/new?vessel=<vid> and …/:rid/edit — "Request crew
// papers" (Skipper-Request artboard). Owner or captain only (the API refuses
// anyone else with 403). Five numbered sections on the left — passage; crew ×
// papers tick grid from a starting set; due date and reminders; status email;
// suggested access — and, on the right, what the first crew member gets and
// the schedule that follows. Save draft, or Send (asked once more first).
// All of the rules are in requestForm.ts and schedule.ts.
//
// Phase 3: …/:rid/edit also edits an OPEN request ("Edit request" on the
// tracker): dates, reminders, status, time zone, adding and removing people —
// but not what someone already asked is asked for (the API allows that only
// in a draft). A time zone field (default the browser's) sets when emails go;
// if the account's own zone is still UTC, it offers to save the browser's
// there too (PUT /api/users/me, as the profile does). Dates that look like a
// slip of the year get a warning, never a block.
// ==============================================

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/AuthProvider';
import type { Member } from '../../../types/papers';
import { apiError } from '../apiError';
import {
  addCrew, addPaper, crewLabel, dateWarnings, emptyForm, formFromDetail, REMIND_CHOICES, removeCrew, removePaper, saveRequest, serverErrorFields,
  toggleRequired, toggleTick, validateForm, withDates,
} from '../requestForm';
import { browserZone, zoneChoices } from '../timezone';
import type { FormErrors, RequestForm, RequestWriter } from '../requestForm';
import { schedulePreview } from '../schedule';
import { daysBetween, firstName, fmtDay, fmtDayYear, isDate, passageLabel, today } from '../dates';
import type { RequestDetail, RequestSet } from '../types';
import { FormError, S, Section, Segmented, SkipperConfirm } from './skipperUi';

const WEEKDAYS = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

export const writer: RequestWriter = {
  create: (vid, input) => api.skipperCreateRequest(vid, input),
  update: (vid, rid, input) => api.skipperUpdateRequest(vid, rid, input),
  addRecipient: (vid, rid, input) => api.skipperAddRecipient(vid, rid, input),
  updateRecipient: (vid, rid, recId, input) => api.skipperUpdateRecipient(vid, rid, recId, input),
  removeRecipient: (vid, rid, recId) => api.skipperRemoveRecipient(vid, rid, recId),
  get: (vid, rid) => api.skipperRequest(vid, rid),
};

const PAPER_NOTES: Record<string, string> = {
  nzeta: 'Crew can answer “not needed” (NZ/AU citizens)',
  travel_insurance: 'Including medical evacuation',
  emergency_contact: 'A short form, not a file',
};

function memberEmail(m: Member) { return m.userEmail ?? m.invitedEmail ?? ''; }

export function RequestFormPage() {
  const { rid } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const todayDate = today();

  const access = useQuery({ queryKey: ['papers-access'], queryFn: () => api.papersAccess() });
  const skipperVessels = (access.data?.vessels ?? []).filter((v) => v.role === 'owner' || v.role === 'captain');
  const vid = params.get('vessel') ?? skipperVessels[0]?.id ?? null;
  const sets = useQuery({ queryKey: ['paper-request-sets'], queryFn: () => api.paperRequestSets(), staleTime: Infinity });
  const catalog = useQuery({ queryKey: ['papers-catalog'], queryFn: () => api.papersCatalog(), staleTime: Infinity });
  const vessel = useQuery({ queryKey: ['papers-vessel', vid], queryFn: () => api.papersVessel(vid as string), enabled: !!vid });
  const existing = useQuery({ queryKey: ['skipper-request', vid, rid], queryFn: () => api.skipperRequest(vid as string, rid as string), enabled: !!vid && !!rid, retry: false });

  const set: RequestSet | null = sets.data?.find((s) => s.key === 'arriving_nz') ?? sets.data?.[0] ?? null;
  const [form, setForm] = useState<RequestForm | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState<null | 'draft' | 'send'>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sent, setSent] = useState<RequestDetail | null>(null);
  /** the draft as last saved on this page (so a failed send never saves a second one) */
  const [draft, setDraft] = useState<RequestDetail | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [adding, setAdding] = useState({ email: '', name: '' });
  const [addPaperType, setAddPaperType] = useState('');
  const [removing, setRemoving] = useState<{ key: string; name: string } | null>(null);
  const zone = browserZone();
  // the account's zone is still the default: offer the browser's (checked), saved with the request
  const offerZone = (user?.timezone ?? 'UTC') === 'UTC' && zone !== 'UTC';
  const [saveZone, setSaveZone] = useState(true);
  const [zoneSaved, setZoneSaved] = useState(false);

  // start the form once its inputs are here
  useEffect(() => {
    if (form || !sets.data) return;
    if (rid) { if (existing.data) { setForm(formFromDetail(existing.data, set)); setSavedAt(existing.data.updatedAt); } return; }
    setForm(emptyForm(set, zone));
  }, [form, sets.data, set, rid, existing.data, zone]);

  const members = useMemo(() => (vessel.data?.members ?? []).filter((m) => (m.status === 'active' || m.status === 'invited') && m.userId !== vessel.data?.me.userId && memberEmail(m)), [vessel.data]);
  const crewTypes = catalog.data?.sections.find((s) => s.key === 'crew')?.types ?? [];

  if (access.data && !skipperVessels.length) {
    return <div className="mx-auto max-w-xl p-6"><FormError>Only a vessel’s owner or captain can ask crew for papers.</FormError></div>;
  }
  if (existing.error) return <div className="mx-auto max-w-xl p-6"><FormError>{apiError(existing.error, 'This request could not be loaded.').message}</FormError></div>;
  if (!form || !vid) return <div className="p-10 text-center text-base text-slate-400" role="status">Loading…</div>;
  if (existing.data && existing.data.status !== 'draft' && existing.data.status !== 'open' && existing.data.status !== 'complete') {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-6">
        <FormError>This request is {existing.data.status === 'cancelled' ? 'cancelled' : 'closed'}, so it can’t be changed.</FormError>
        <Link to={`/dashboard/papers/requests/${rid}?vessel=${vid}`} className={S.link}>Open the request</Link>
      </div>
    );
  }

  const f = form;
  /** an open request: people can come and go, but what someone was asked for is fixed */
  const sentMode = !!existing.data && existing.data.status !== 'draft';
  const locked = (key: string) => sentMode && !!f.crew.find((c) => c.key === key)?.recipientId;
  const warnings = dateWarnings(f, todayDate);
  const zones = zoneChoices([f.timezone, zone]);
  const update = (next: RequestForm) => { setForm(next); if (Object.keys(errors).length) setErrors({}); };
  const passage = passageLabel(f.from, f.to);
  const first = f.crew[0];
  const firstItems = first ? f.papers.filter((p) => (f.ticks[first.key] ?? []).includes(p.type)) : [];
  const schedule = schedulePreview({ sentOn: todayDate, dueOn: isDate(f.dueOn) ? f.dueOn : null, remindEveryDays: f.remindEveryDays, dailyFinalDays: f.dailyFinal ? 3 : 0,
    cadence: f.cadence === 'every_3_days' ? 'every_n_days' : f.cadence, weekday: f.cadence === 'weekly' ? f.statusWeekday : null, everyDays: f.cadence === 'every_3_days' ? 3 : null });
  const addable = crewTypes.filter((t) => !f.papers.some((p) => p.type === t.key));
  const unpicked = members.filter((m) => !f.crew.some((c) => c.email.trim().toLowerCase() === memberEmail(m).toLowerCase()));
  const inviteErr = errors.adding;
  const fieldErr = (k: string) => errors[k] ? <span id={`err-${k}`} className={S.err}>{errors[k]}</span>
    : warnings[k as keyof typeof warnings] ? <span id={`warn-${k}`} className="text-sm text-amber-300">{warnings[k as keyof typeof warnings]}</span> : null;
  const aria = (k: string) => (errors[k] ? { 'aria-invalid': true as const, 'aria-describedby': `err-${k}` } : {});
  const field = (k: string, label: string, control: React.ReactNode, hint?: string | null) => (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`rq-${k}`} className="text-sm font-semibold text-slate-200">{label}</label>
      {control}
      {hint && <span className="text-sm text-slate-400">{hint}</span>}
      {fieldErr(k)}
    </div>
  );

  const focusFirst = (e: FormErrors) => {
    const k = Object.keys(e)[0];
    if (!k) return;
    const id = k.startsWith('crew:') ? `crew-${k.slice(5)}` : k === 'crew' ? 'crew-add-email' : `rq-${k}`;
    setTimeout(() => (document.getElementById(id) ?? document.getElementById('rq-form-error'))?.focus(), 0);
  };

  /** The account's zone, once, if the skipper left the offer ticked. A failure here never stops the request. */
  const maybeSaveZone = async () => {
    if (!offerZone || !saveZone || zoneSaved || f.timezone !== zone) return;
    try { await api.updateProfile({ timezone: zone }); setZoneSaved(true); } catch { /* the request is saved; the profile can be set later */ }
  };

  const save = async (mode: 'draft' | 'send' | 'edit'): Promise<RequestDetail | null> => {
    const e = validateForm(f, mode, { today: todayDate, myEmail: user?.email });
    setErrors(e);
    if (Object.keys(e).length) { focusFirst(e); return null; }
    try {
      const saved = await saveRequest(writer, vid, draft ?? existing.data ?? null, f, { sent: sentMode });
      await maybeSaveZone();
      setDraft(saved);
      qc.setQueryData(['skipper-request', vid, saved.id], saved);
      qc.invalidateQueries({ queryKey: ['skipper-requests', vid] });
      setSavedAt(saved.updatedAt ?? new Date().toISOString());
      // A new draft gets its own address — but not mid-send, which would remount the page.
      if (!rid && mode === 'draft') navigate(`/dashboard/papers/requests/${saved.id}/edit?vessel=${vid}`, { replace: true });
      return saved;
    } catch (err) {
      const info = apiError(err, 'The request could not be saved.');
      const mapped = serverErrorFields({ ...info, message: info.message }, f);
      setErrors(mapped); focusFirst(mapped);
      return null;
    }
  };

  const onDraft = async () => { setBusy('draft'); await save('draft'); setBusy(null); };
  const onSaveSent = async () => {
    setBusy('draft');
    const saved = await save('edit');
    setBusy(null);
    if (saved) navigate(`/dashboard/papers/requests/${saved.id}?vessel=${vid}`);
  };
  const onSend = () => {
    const e = validateForm(f, 'send', { today: todayDate, myEmail: user?.email });
    setErrors(e);
    if (Object.keys(e).length) { focusFirst(e); return; }
    setConfirmSend(true);
  };
  const doSend = async () => {
    setBusy('send');
    const saved = await save('send');
    if (saved) {
      try {
        const out = await api.skipperSendRequest(vid, saved.id);
        qc.setQueryData(['skipper-request', vid, out.id], out);
        qc.invalidateQueries({ queryKey: ['skipper-requests', vid] });
        setSent(out);
      } catch (err) {
        const mapped = serverErrorFields(apiError(err, 'The request could not be sent.'), f);
        setErrors(mapped);
        focusFirst(mapped);
      }
    }
    setConfirmSend(false);
    setBusy(null);
  };

  if (sent) {
    const failed = sent.recipients.filter((r) => sent.emailFailures?.includes(r.id));
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
        <div className={`${S.card} space-y-3 p-6`} role="status">
          <h1 className="m-0 text-2xl font-bold text-white">Sent to {sent.recipients.length} crew</h1>
          <p className="m-0 text-base leading-relaxed text-slate-300">Each of them has an email with their own link. Nothing is shared with you until they share it, and you’ll see each paper arrive on the request.</p>
          {failed.length > 0 && <FormError>We couldn’t email {failed.map((r) => r.name ?? r.email).join(', ')}. Open the request and use Resend link.</FormError>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Link to={`/dashboard/papers/requests/${sent.id}?vessel=${vid}`} className={S.primary}>Open the request</Link>
            <Link to={`/dashboard/papers?vessel=${vid}`} className={S.ghost}>Back to Ship’s Papers</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1280px] space-y-6 p-3 sm:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-2">
          <nav aria-label="Breadcrumb" className={S.label}><Link to={`/dashboard/papers?vessel=${vid}`} className="hover:text-white">Ship’s Papers</Link> › <Link to={`/dashboard/papers/requests?vessel=${vid}`} className="hover:text-white">Crew papers requests</Link></nav>
          <h1 className="m-0 text-3xl font-bold text-white">{sentMode ? 'Edit request' : 'Request crew papers'}</h1>
          {sentMode && <p className="m-0 text-base text-slate-300">This request has been sent. You can change its dates, reminders and status email, add people (they’re emailed at once) or take someone off. What each person was asked for stays as it is.</p>}
          {skipperVessels.length > 1 && !rid && (
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">Vessel
              <select value={vid} onChange={(e) => navigate(`/dashboard/papers/requests/new?vessel=${e.target.value}`, { replace: true })} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-800 px-3 text-base text-white">
                {skipperVessels.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
          )}
        </div>
        {savedAt && !sentMode && <span className={S.note} role="status">Saved as a draft · {fmtDay(savedAt.slice(0, 10))}</span>}
      </div>

      <div id="rq-form-error" tabIndex={-1}><FormError>{errors.form}</FormError></div>

      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="flex min-w-0 flex-col gap-5 xl:w-[800px] xl:shrink-0">
          <Section num={1} title="Passage">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {field('from', 'From', <input id="rq-from" className={S.input} value={f.from} onChange={(e) => update({ ...f, from: e.target.value })} placeholder="Nouméa, New Caledonia" {...aria('from')} />)}
              {field('to', 'To', <input id="rq-to" className={S.input} value={f.to} onChange={(e) => update({ ...f, to: e.target.value })} placeholder="Opua, New Zealand" {...aria('to')} />)}
              {field('departOn', 'Depart', <input id="rq-departOn" type="date" className={S.input} value={f.departOn} onChange={(e) => update(withDates(f, { departOn: e.target.value }, todayDate))} {...aria('departOn')} />)}
              {field('arriveOn', 'Arrive (about)', <input id="rq-arriveOn" type="date" className={S.input} value={f.arriveOn} min={f.departOn || undefined} onChange={(e) => update(withDates(f, { arriveOn: e.target.value }, todayDate))} {...aria('arriveOn')} />)}
            </div>
          </Section>

          <Section num={2} title="Crew and the papers you need from each">
            {set && <div className="flex flex-col gap-1"><span className={S.label}>Starting set</span><span className="text-[15px] font-semibold text-amber-300">{set.label}</span></div>}
            {f.crew.length === 0 ? (
              <p className="m-0 text-base text-slate-300">Add the crew below — people aboard {vessel.data?.vessel.name ?? 'the vessel'}, or anyone by email.</p>
            ) : (
              <div className="-mx-1 overflow-x-auto px-1">
                <table className="w-full min-w-[560px] border-collapse text-left">
                  <caption className="sr-only">Papers asked of each crew member</caption>
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th scope="col" className="w-[260px] pb-3 align-bottom"><span className={S.label}>Paper</span></th>
                      {f.crew.map((c) => (
                        <th key={c.key} scope="col" className="px-1 pb-3 text-center align-bottom font-normal">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[15px] font-semibold text-white">{crewLabel(c)}</span>
                            <span className="text-[13px] text-slate-400">{c.note ?? (c.source === 'new' ? 'new · by email' : c.source === 'aboard' ? 'aboard' : c.email)}</span>
                            <button type="button" onClick={() => (locked(c.key) ? setRemoving({ key: c.key, name: crewLabel(c) }) : update(removeCrew(f, c.key)))} aria-label={`Remove ${crewLabel(c)} from the request`} className="min-h-[32px] px-2 text-[13px] font-semibold text-slate-400 hover:text-red-300">Remove</button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {f.papers.map((p) => (
                      <tr key={p.type} className="border-b border-slate-700/60">
                        <th scope="row" className="py-3 pr-3 align-middle font-normal">
                          <div className="flex flex-col gap-1">
                            <span className="text-base font-semibold text-white">{p.label}</span>
                            {p.type === 'passport' ? (
                              <label className="flex flex-wrap items-center gap-2 text-sm text-slate-400">Valid until at least
                                <input id="rq-passportValidUntil" type="date" value={f.passportValidUntil} onChange={(e) => update({ ...f, passportValidUntil: e.target.value, passportTouched: true })}
                                  className="min-h-[36px] rounded-md border border-slate-600 bg-slate-900 px-2 text-sm text-white" />
                              </label>
                            ) : PAPER_NOTES[p.type] && <span className="text-sm text-slate-400">{PAPER_NOTES[p.type]}</span>}
                            {sentMode ? <span className="text-sm text-slate-400">{p.required ? 'Required' : 'Optional'}</span> : (
                              <span className="flex flex-wrap gap-3">
                                <button type="button" aria-pressed={!p.required} onClick={() => update(toggleRequired(f, p.type))} className="min-h-[32px] text-sm font-semibold text-slate-300 hover:text-white">{p.required ? 'Required' : 'Optional'} · change</button>
                                <button type="button" onClick={() => update(removePaper(f, p.type))} aria-label={`Stop asking for ${p.label}`} className="min-h-[32px] text-sm font-semibold text-slate-400 hover:text-red-300">Remove</button>
                              </span>
                            )}
                          </div>
                        </th>
                        {f.crew.map((c) => {
                          const on = (f.ticks[c.key] ?? []).includes(p.type);
                          const fixed = locked(c.key);
                          return (
                            <td key={c.key} className="px-1 py-3 text-center">
                              <button type="button" aria-pressed={on} aria-label={`${p.label} from ${crewLabel(c)}`} disabled={fixed} title={fixed ? 'Already asked — this can’t change now' : undefined} onClick={() => update(toggleTick(f, c.key, p.type))}
                                className={`inline-flex h-11 w-11 items-center justify-center rounded-[10px] border focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-60 ${on ? 'border-amber-400 bg-amber-400 text-slate-900' : 'border-slate-500 text-transparent hover:border-slate-300'}`}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {f.crew.map((c) => errors[`crew:${c.key}`] && <p key={c.key} id={`crew-${c.key}`} tabIndex={-1} className={`m-0 ${S.err}`}>{crewLabel(c)} ({c.email}): {errors[`crew:${c.key}`]}</p>)}
            {fieldErr('crew')}

            <div className="flex flex-col gap-4 border-t border-slate-700 pt-4 lg:flex-row lg:items-end">
              {addable.length > 0 && (
                <label className={`${S.field} lg:w-64`}>Add a paper
                  <span className="flex gap-2">
                    <select value={addPaperType} onChange={(e) => setAddPaperType(e.target.value)} className={S.input}>
                      <option value="">Choose…</option>
                      {addable.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                    </select>
                    <button type="button" className={S.ghost} disabled={!addPaperType} onClick={() => {
                      const t = crewTypes.find((x) => x.key === addPaperType); if (!t) return;
                      update(addPaper(f, { type: t.key, label: t.label, required: true }, { sent: sentMode })); setAddPaperType('');
                    }}>Add</button>
                  </span>
                </label>
              )}
              {unpicked.length > 0 && (
                <label className={`${S.field} lg:w-64`}>Add someone aboard
                  <select value="" onChange={(e) => {
                    const m = unpicked.find((x) => x.id === e.target.value); if (!m) return;
                    update(addCrew(f, { email: memberEmail(m), name: m.userName ?? '', source: 'aboard', note: `aboard · ${m.role}` }));
                  }} className={S.input}>
                    <option value="">Choose…</option>
                    {unpicked.map((m) => <option key={m.id} value={m.id}>{m.userName ?? memberEmail(m)}</option>)}
                  </select>
                </label>
              )}
              <form className="flex flex-1 flex-col gap-1.5" onSubmit={(e) => {
                e.preventDefault();
                if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(adding.email.trim())) { setErrors({ adding: 'Enter their email address' }); return; }
                update(addCrew(f, { email: adding.email.trim(), name: adding.name.trim(), source: 'new' })); setAdding({ email: '', name: '' });
              }}>
                <span className="text-sm font-semibold text-slate-200">Add crew by email</span>
                <span className="flex flex-col gap-2 sm:flex-row">
                  <input aria-label="Their name" placeholder="Name" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} className={S.input} />
                  <input id="crew-add-email" aria-label="Their email" type="email" placeholder="name@example.com" value={adding.email} onChange={(e) => setAdding({ ...adding, email: e.target.value })} className={S.input} aria-invalid={inviteErr ? true : undefined} />
                  <button type="submit" className={S.ghost}>Add</button>
                </span>
                {inviteErr && <span className={S.err}>{inviteErr}</span>}
              </form>
            </div>
          </Section>

          <Section num={3} title="Due date and crew reminders">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {field('dueOn', 'Papers due', <input id="rq-dueOn" type="date" className={S.input} value={f.dueOn} max={f.arriveOn || undefined} onChange={(e) => update({ ...f, dueOn: e.target.value, dueTouched: true })} {...aria('dueOn')} />,
                isDate(f.dueOn) && isDate(f.departOn) && daysBetween(f.dueOn, f.departOn) > 0 ? `${daysBetween(f.dueOn, f.departOn)} days before you sail` : null)}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold text-slate-200" id="rq-remind-label">Remind crew every</span>
                <Segmented label="Remind crew every" value={f.remindEveryDays} onChange={(v) => update({ ...f, remindEveryDays: v })}
                  options={REMIND_CHOICES.map((d) => ({ value: d, label: d === 7 ? 'Week' : `${d} days` }))} />
                {fieldErr('reminders')}
              </div>
            </div>
            <label className="flex min-h-[44px] items-center gap-3 text-[15px] text-slate-200">
              <input type="checkbox" checked={f.dailyFinal} onChange={(e) => update({ ...f, dailyFinal: e.target.checked })} className="h-5 w-5 rounded border-slate-500 bg-slate-900 text-amber-400" />
              Remind daily in the last 3 days before the due date
            </label>
            {field('timezone', 'Time zone for the emails', (
              <select id="rq-timezone" className={S.input} value={f.timezone} onChange={(e) => update({ ...f, timezone: e.target.value })} {...aria('timezone')}>
                {zones.map((z) => <option key={z} value={z}>{z === zone ? `${z} (this device)` : z}</option>)}
              </select>
            ), `Reminders and your status email go at 08:00 ${f.timezone} time.`)}
            {offerZone && f.timezone === zone && !zoneSaved && (
              <label className="flex min-h-[44px] items-center gap-3 text-[15px] text-slate-200">
                <input type="checkbox" checked={saveZone} onChange={(e) => setSaveZone(e.target.checked)} className="h-5 w-5 rounded border-slate-500 bg-slate-900 text-amber-400" />
                Also make {zone} my account’s time zone (it says UTC now)
              </label>
            )}
            <p className={`m-0 ${S.note}`}>Reminders list only what that person still owes and stop once they’re done.</p>
          </Section>

          <Section num={4} title="Your status email">
            <Segmented label="Your status email" value={f.cadence} onChange={(v) => update({ ...f, cadence: v })}
              options={[{ value: 'each_upload', label: 'Every upload' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'every_3_days', label: 'Every 3 days' }]} />
            {f.cadence === 'weekly' && (
              <label className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-200">On
                <select value={f.statusWeekday} onChange={(e) => update({ ...f, statusWeekday: Number(e.target.value) })} className="min-h-[44px] rounded-lg border border-slate-600 bg-slate-900 px-3 text-base text-white">
                  {WEEKDAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
                </select>
              </label>
            )}
            {fieldErr('status')}
            <p className={`m-0 ${S.note}`}>You also always get one when everything is in, and one on the due date.</p>
          </Section>

          <Section num={5} title="How long you’d like access">
            <Segmented label="How long you’d like access" value={f.access} onChange={(v) => update({ ...f, access: v })}
              options={[{ value: 'on_arrival', label: 'Until we arrive' }, { value: 'plus30', label: '30 days after arrival' }, { value: 'plus90', label: '90 days after arrival' }]} />
            {fieldErr('access')}
            <p className={`m-0 ${S.note}`}>This is a suggestion. Each crew member sets the actual end date. You’ll be warned 3 days before any access ends.</p>
          </Section>
        </div>

        <aside className="flex min-w-0 flex-grow flex-col gap-5" aria-label="Preview">
          <div className={`${S.card} flex flex-col gap-4 p-5`}>
            <span className={S.label}>{first ? `${firstName(crewLabel(first), 'They')} will get` : 'Each crew member gets'}</span>
            <div className="flex flex-col gap-2.5 rounded-xl bg-white p-4 text-slate-800">
              <span className="text-[13px] text-slate-500">From: {user?.name ?? 'You'} via MyPolaris</span>
              <span className="text-[17px] font-bold leading-snug">{user?.name ?? 'Your skipper'} asked for your papers for {passage ?? 'the passage'}</span>
              <span className="text-sm leading-relaxed text-slate-600">
                {firstItems.length ? firstItems.map((p) => p.label).join(' · ') : 'The papers you tick'}.{isDate(f.dueOn) ? ` Due ${fmtDay(f.dueOn)}.` : ''}
              </span>
              <span className="self-start rounded-lg bg-blue-700 px-4 py-2.5 text-[15px] font-semibold text-white" aria-hidden="true">Provide my papers</span>
            </div>
            <p className={`m-0 ${S.note}`}>They sign in with a code sent to that address, so a forwarded link is useless to anyone else. Only you and they will see what they share.</p>
          </div>

          <div className={`${S.card} flex flex-col gap-3.5 p-5`}>
            <span className={S.label}>Schedule</span>
            {schedule.rows.length === 0 ? <p className={`m-0 ${S.note}`}>Choose the dates to see when reminders go.</p> : (
              <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-[15px]">
                {schedule.rows.map((r) => (
                  <li key={`${r.from}-${r.label}`} className={`flex justify-between gap-3 ${r.due ? 'font-semibold text-amber-300' : 'text-slate-200'}`}>
                    <span className="font-mono">{r.to ? `${fmtDay(r.from)} – ${fmtDay(r.to)}` : r.from === todayDate && r.label === 'Request sent' ? `${fmtDay(r.from)} (today)` : fmtDay(r.from)}</span>
                    <span className="text-right">{r.label}</span>
                  </li>
                ))}
              </ul>
            )}
            {schedule.note && <p className={`m-0 ${S.note}`}>{schedule.note}</p>}
            <p className={`m-0 ${S.note}`}>Emails go at 08:00 {f.timezone} time. Crew who are finished stop getting reminders.</p>
          </div>

          {sentMode ? (
            <div className="flex flex-col gap-2.5">
              <button type="button" className={`${S.primary} min-h-[52px] text-[17px]`} onClick={() => void onSaveSent()} disabled={!!busy}>{busy ? 'Saving…' : 'Save changes'}</button>
              <Link to={`/dashboard/papers/requests/${rid}?vessel=${vid}`} className={S.ghost}>Back to the request</Link>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              <button type="button" className={`${S.primary} min-h-[52px] text-[17px]`} onClick={onSend} disabled={!!busy}>Send to {f.crew.length} crew</button>
              <button type="button" className={S.ghost} onClick={() => void onDraft()} disabled={!!busy}>{busy === 'draft' ? 'Saving…' : 'Save draft'}</button>
            </div>
          )}
        </aside>
      </div>

      {removing && (
        <SkipperConfirm title={`Take ${removing.name} off this request?`} confirmLabel="Take off" danger onCancel={() => setRemoving(null)}
          onConfirm={() => { update(removeCrew(f, removing.key)); setRemoving(null); }}>
          When you save, {removing.name}’s link stops working and anything they shared with you under this request is withdrawn at once. Their papers stay in their own papers.
        </SkipperConfirm>
      )}
      {confirmSend && (
        <SkipperConfirm title={`Send to ${f.crew.length} crew?`} confirmLabel="Send now" busy={busy === 'send'} onConfirm={() => void doSend()} onCancel={() => setConfirmSend(false)}>
          Each of {f.crew.map(crewLabel).join(', ')} gets an email now with their own link{isDate(f.dueOn) ? `, asking for their papers by ${fmtDayYear(f.dueOn)}` : ''}. You can add people later, but not change what each is asked for.
        </SkipperConfirm>
      )}
    </div>
  );
}

export default RequestFormPage;
